import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type TooltipDirection = 'top' | 'right' | 'bottom' | 'left';

@Component({
  selector: 'dmcom-tooltip-content',
  standalone: true,
  imports: [NgClass],
  template: `
    <div
      class="flex items-center"
      [ngClass]="{
        'px-0 py-4': direction() === 'top' || direction() === 'bottom',
        'px-4 py-0': direction() === 'left' || direction() === 'right',
      }"
    >
      <div
        class="
        bg-tooltip-background text-shades-white typo-caption font-semibold
          relative rounded whitespace-nowrap
          h-32 px-16 py-8 flex items-center justify-center shrink-0
          tablet-portrait:h-24 tablet-portrait:px-8 tablet-portrait:py-4
        "
      >
        {{ text() }}
      </div>

      <div
        class="absolute flex items-center justify-center shrink-0 size-12 transform"
        [ngClass]="{
          'top-1/2 -translate-y-1/2': ['left', 'right'].includes(direction()),
          'left-1/2 -translate-x-1/2': ['top', 'bottom'].includes(direction()),
          'right-0': direction() === 'left',
          'left-0': direction() === 'right',
          'top-0': direction() === 'bottom',
          'bottom-0': direction() === 'top',
        }"
      >
        <div class="bg-tooltip-background size-8 rotate-45"></div>
      </div>
    </div>
  `,
})
export class TooltipContentComponent {
  text = model<string>('');
  direction = model<TooltipDirection>('top');
}

// DEPRECATED, use matTooltip instead
// this is still used in the prompt until refactored for matTooltip
@Directive({
  selector: '[dmcomTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy, AfterViewInit {
  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  text = input<string>('dmcomTooltip', { alias: 'dmcomTooltip' });
  direction = input<TooltipDirection>('top');
  alwaysOn = input<boolean>(false);
  disabled = input<boolean>(false);
  showDelay = input<number>(0);
  hideDelay = input<number>(250);

  private overlayRef: OverlayRef | null = null;
  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public readonly isAlwaysOn = signal(false);

  constructor() {
    // Track changes to input signals and update isAlwaysOn accordingly
    effect(() => {
      this.isAlwaysOn.set(this.alwaysOn() && !!this.text() && !this.disabled());
    });
    effect(() => {
      const shouldShow = this.alwaysOn() && !!this.text() && !this.disabled();
      if (shouldShow) {
        this.show();
      } else {
        this.hide();
      }
    });
  }

  ngAfterViewInit() {
    if (this.isAlwaysOn()) {
      this.show();
    }
  }

  ngOnDestroy() {
    this.clearTimeouts();
    this.hide();
  }

  // @HostListener('mouseenter')
  // onMouseEnter() {
  //   if (this.shouldShowTooltip()) {
  //     this.clearTimeouts();
  //     this.showTimeoutId = setTimeout(() => {
  //       this.show();
  //     }, this.showDelay());
  //   }
  // }

  // @HostListener('focus')
  // onFocus() {
  //   if (this.shouldShowTooltip()) {
  //     this.clearTimeouts();
  //     this.show();
  //   }
  // }

  // @HostListener('mouseleave')
  // @HostListener('blur')
  // onBlur() {
  //   if (!this.isAlwaysOn()) {
  //     this.clearTimeouts();
  //     this.hideTimeoutId = setTimeout(() => {
  //       this.hide();
  //     }, this.hideDelay());
  //   }
  // }

  // @HostListener('keydown.escape')
  // onEscape() {
  //   if (!this.isAlwaysOn()) {
  //     this.hide();
  //   }
  // }

  private shouldShowTooltip(): boolean {
    return !this.disabled() && !!this.text() && this.text().trim() !== '';
  }

  private show() {
    console.log('show', this.shouldShowTooltip());
    if (this.overlayRef?.hasAttached() ?? !this.shouldShowTooltip()) {
      return;
    }

    this.createOverlay();

    if (this.overlayRef) {
      const tooltipComponent = this.overlayRef.attach(
        new ComponentPortal(TooltipContentComponent),
      );
      // Explicitly set values for input signals to update the tooltip component
      tooltipComponent.instance.text.set(this.text());
      tooltipComponent.instance.direction.set(this.direction());

      // Handle overlay detachment on outside clicks (except for always-on tooltips)
      if (!this.isAlwaysOn()) {
        this.overlayRef
          .outsidePointerEvents()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.hide();
          });
      }

      // Handle ESC key
      this.overlayRef
        .keydownEvents()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => {
          if (event.key === 'Escape' && !this.isAlwaysOn()) {
            this.hide();
          }
        });
    }
  }

  private hide() {
    this.clearTimeouts();
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private createOverlay() {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.getPositionStrategy();
    const scrollStrategy = this.overlay.scrollStrategies.reposition({
      // scrollThreshold: 20,
      autoClose: !this.isAlwaysOn(),
    });

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: false,
      panelClass: 'dmcom-tooltip-overlay',
    });

    this.overlayRef = this.overlay.create(overlayConfig);

    this.overlayRef
      .detachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.overlayRef) {
          this.overlayRef.dispose();
          this.overlayRef = null;
        }
      });
  }

  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    const direction = this.direction();
    const positions = this.getPositionsForDirection(direction);

    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8)
      .withDefaultOffsetX(0)
      .withDefaultOffsetY(0);
  }

  private getPositionsForDirection(
    direction: TooltipDirection,
  ): ConnectedPosition[] {
    const spacing = 8;

    const positions: Record<string, ConnectedPosition[]> = {
      top: [
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -spacing,
        },
        // Fallback positions
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: spacing,
        },
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: spacing,
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -spacing,
        },
      ],
      bottom: [
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: spacing,
        },
        // Fallback positions
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -spacing,
        },
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: spacing,
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -spacing,
        },
      ],
      left: [
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -spacing,
        },
        // Fallback positions
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: spacing,
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -spacing,
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: spacing,
        },
      ],
      right: [
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: spacing,
        },
        // Fallback positions
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -spacing,
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -spacing,
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: spacing,
        },
      ],
    };

    return positions[direction] || positions['top'];
  }

  private clearTimeouts() {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }
}
